'use client'

import { useState, useEffect, ReactNode } from "react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet"
import { SSEProvider } from "@/components/providers/sse-provider"
import { UserMenuClient } from "@/components/auth/user-menu-client"
import {
  Bot,
  FolderGit2,
  Container,
  Activity,
  FlaskConical,
  Settings,
  DollarSign,
  RefreshCw,
  Menu,
  Server,
  Play,
  PanelLeftClose
} from "lucide-react"
import { SettingsTab } from "@/components/settings/settings-tab"
import * as VisuallyHidden from "@radix-ui/react-visually-hidden"

// Show tests tab in development or when ALLOW_TESTS is set
const SHOW_TESTS = process.env.NODE_ENV === 'development' || process.env.NEXT_PUBLIC_ALLOW_TESTS === 'true'

type TabValue = 'ai' | 'projects' | 'containers' | 'monitoring' | 'runs' | 'updates' | 'usage' | 'tests' | 'settings'

const VALID_TABS: TabValue[] = SHOW_TESTS
    ? ['ai', 'projects', 'containers', 'monitoring', 'runs', 'updates', 'usage', 'tests', 'settings']
    : ['ai', 'projects', 'containers', 'monitoring', 'runs', 'updates', 'usage', 'settings']

interface NavItem {
  value: TabValue
  label: string
  icon: React.ComponentType<{ className?: string }>
  showInDev?: boolean
}

const navItems: NavItem[] = [
  { value: 'ai', label: 'AI Ops', icon: Bot },
  { value: 'projects', label: 'Projects', icon: FolderGit2 },
  { value: 'containers', label: 'Containers', icon: Container },
  { value: 'monitoring', label: 'Monitoring', icon: Activity },
  { value: 'runs', label: 'Runs', icon: Play },
  { value: 'updates', label: 'Updates', icon: RefreshCw },
  { value: 'usage', label: 'Usage', icon: DollarSign },
  { value: 'tests', label: 'Tests', icon: FlaskConical, showInDev: true },
  { value: 'settings', label: 'Settings', icon: Settings },
]

function getTabFromUrl(): TabValue {
  if (typeof window === 'undefined') return 'ai'
  const params = new URLSearchParams(window.location.search)
  const tab = params.get('tab') as TabValue | null
  return tab && VALID_TABS.includes(tab) ? tab : 'ai'
}

interface SidebarNavProps {
  projectsContent: ReactNode
  containersContent: ReactNode
  monitoringContent: ReactNode
  aiContent: ReactNode
  runsContent: ReactNode
  updatesContent: ReactNode
  usageContent: ReactNode
  testsContent?: ReactNode
  user: {
    name?: string | null
    email?: string | null
    image?: string | null
  }
  versionInfo: {
    commitHash: string
    envSuffix: string
  }
  initialTab?: string
}

export function SidebarNav({
  projectsContent,
  containersContent,
  monitoringContent,
  aiContent,
  runsContent,
  updatesContent,
  usageContent,
  testsContent,
  user,
  versionInfo,
  initialTab
}: SidebarNavProps) {
  // Use initialTab from server if valid, otherwise default to 'ai'
  const getInitialTab = (): TabValue => {
    if (initialTab && VALID_TABS.includes(initialTab as TabValue)) {
      return initialTab as TabValue
    }
    return 'ai'
  }

  const [currentTab, setCurrentTab] = useState<TabValue>(getInitialTab)
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [isMobileOpen, setIsMobileOpen] = useState(false)

  const handleTabChange = (tab: TabValue) => {
    setCurrentTab(tab)
    setIsMobileOpen(false)
    const url = new URL(window.location.href)
    url.searchParams.set('tab', tab)
    window.history.replaceState({}, '', url.toString())
  }

  useEffect(() => {
    // Sync with URL on mount (handles browser back/forward)
    const handlePopState = () => {
      setCurrentTab(getTabFromUrl())
    }
    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [])

  const filteredNavItems = navItems.filter(item =>
    !item.showInDev || SHOW_TESTS
  )

  const renderContent = () => {
    switch (currentTab) {
      case 'ai':
        return aiContent
      case 'projects':
        return projectsContent
      case 'containers':
        return containersContent
      case 'monitoring':
        return monitoringContent
      case 'runs':
        return runsContent
      case 'updates':
        return updatesContent
      case 'usage':
        return usageContent
      case 'tests':
        return SHOW_TESTS && testsContent ? testsContent : null
      case 'settings':
        return <SettingsTab />
      default:
        return null
    }
  }

  const { commitHash, envSuffix } = versionInfo

  const EnvBadge = ({ compact = false }: { compact?: boolean }) => (
    <div className={cn(
      "flex items-center text-xs text-muted-foreground",
      compact ? "flex-col gap-1" : "gap-1.5"
    )}>
      <span className={cn(
        "px-1.5 py-0.5 rounded font-medium",
        envSuffix === 'prod'
          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
          : envSuffix === 'stage'
          ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
          : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
      )}>
        {envSuffix}
      </span>
      <span className={cn(
        "font-mono bg-muted px-1.5 py-0.5 rounded",
        compact && "text-[10px]"
      )}>
        {commitHash}
      </span>
    </div>
  )

  const NavLinks = ({ collapsed = false, onSelect }: { collapsed?: boolean, onSelect?: () => void }) => (
    <nav className="flex flex-col gap-1">
      {filteredNavItems.map((item) => {
        const Icon = item.icon
        const isActive = currentTab === item.value
        return (
          <button
            key={item.value}
            onClick={() => {
              handleTabChange(item.value)
              onSelect?.()
            }}
            className={cn(
              "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
              "hover:bg-accent hover:text-accent-foreground",
              isActive
                ? "bg-accent text-accent-foreground"
                : "text-muted-foreground",
              collapsed && "justify-center px-2"
            )}
            title={collapsed ? item.label : undefined}
          >
            <Icon className="h-4 w-4 shrink-0" />
            {!collapsed && <span>{item.label}</span>}
          </button>
        )
      })}
    </nav>
  )

  return (
    <SSEProvider>
      <div className="flex h-dvh">
        {/* Desktop Sidebar */}
        <aside
          className={cn(
            "hidden md:flex flex-col border-r bg-sidebar shrink-0",
            isCollapsed ? "w-14" : "w-56"
          )}
        >
          {/* Header with logo - click to toggle collapse */}
          <div
            className={cn(
              "flex h-14 items-center border-b shrink-0 cursor-pointer hover:bg-accent/50 transition-colors",
              isCollapsed ? "justify-center px-2" : "justify-between px-3"
            )}
            onClick={() => setIsCollapsed(!isCollapsed)}
          >
            <div className="flex items-center gap-2">
              <Server className="h-5 w-5 shrink-0 text-primary" />
              {!isCollapsed && (
                <span className="font-semibold text-lg whitespace-nowrap">VDS Manager</span>
              )}
            </div>
            {!isCollapsed && (
              <PanelLeftClose className="h-4 w-4 shrink-0 text-muted-foreground" />
            )}
          </div>

          {/* Navigation */}
          <div className="flex-1 overflow-y-auto p-2">
            <NavLinks collapsed={isCollapsed} />
          </div>

          {/* Footer with env badge and user */}
          <div className="border-t p-2 space-y-2 shrink-0">
            <div className="flex justify-center">
              <EnvBadge compact={isCollapsed} />
            </div>
            <UserMenuClient user={user} collapsed={isCollapsed} />
          </div>
        </aside>

        {/* Mobile Header */}
        <div
          className="md:hidden fixed top-0 left-0 right-0 z-50 flex items-center justify-between h-14 px-4 border-b bg-background"
          style={{ paddingTop: 'env(safe-area-inset-top)' }}
        >
          <div className="flex items-center gap-2">
            <Sheet open={isMobileOpen} onOpenChange={setIsMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="h-9 w-9">
                  <Menu className="h-5 w-5" />
                  <span className="sr-only">Toggle menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-64 p-0 flex flex-col">
                <VisuallyHidden.Root>
                  <SheetTitle>Navigation Menu</SheetTitle>
                </VisuallyHidden.Root>
                <div className="flex h-14 items-center gap-2 px-4 border-b shrink-0">
                  <Server className="h-5 w-5 shrink-0 text-primary" />
                  <span className="font-semibold text-lg">VDS Manager</span>
                </div>
                <div className="flex-1 overflow-y-auto p-2">
                  <NavLinks onSelect={() => setIsMobileOpen(false)} />
                </div>
                <div className="border-t p-3 space-y-3 shrink-0">
                  <EnvBadge />
                  <UserMenuClient user={user} />
                </div>
              </SheetContent>
            </Sheet>
            <Server className="h-5 w-5 text-primary" />
            <span className="font-semibold">VDS</span>
          </div>
          <span className="text-sm text-muted-foreground">
            {filteredNavItems.find(item => item.value === currentTab)?.label}
          </span>
        </div>

        {/* Main Content */}
        <main className="flex-1 overflow-hidden flex flex-col pt-[calc(3.5rem+env(safe-area-inset-top))] md:pt-0">
          <div
            className={cn(
              "flex-1",
              currentTab === 'ai' ? "p-0 overflow-hidden" : "p-4 md:p-6 overflow-y-auto"
            )}
            style={currentTab !== 'ai' ? { paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom))' } : undefined}
          >
            {/* AI tab uses forceMount pattern to preserve chat state */}
            <div
              className={cn(
                currentTab === 'ai' ? "h-full" : "hidden"
              )}
            >
              {aiContent}
            </div>
            {currentTab !== 'ai' && renderContent()}
          </div>
        </main>
      </div>
    </SSEProvider>
  )
}
